import React from 'react';
import { Grid } from '@material-ui/core';
import { useInfiniteQuery, useQuery } from 'react-query';
import { getCluster } from 'services/cluster';
import {
  postMaterialClassFilterOptions,
  postGroupFilterOptions,
  postDescriptionFilterOptions,
} from 'services/stock-position';
import { useDispatchFilter, useSelectFilter } from 'hooks/planning/useFilter';
import MultipleCheckboxWithSubgroup from 'components/atoms/MultipleCheckboxWithSubgroup';
import { IFilterOptions } from 'interfaces/SelectOptions';
import FilterBarItem from 'components/molecules/FilterBarItem';
import {
  IStockPositionFilterData,
  IStockPositionFilterOptions,
} from 'interfaces/StockPosition';
import qs from 'qs';
import { debounce } from 'lodash';
import useStyles from './style';

const TAKE = 10;

const mapFilterResult = (result: IStockPositionFilterOptions[]) =>
  result.map(item => ({
    label: item?.description,
    value: item?.id,
  }));

const StockPositionTopContainer = ({
  updatedAt,
}: {
  updatedAt: Date | null;
}) => {
  const classes = useStyles();
  const [inputFieldValues, setInputFieldValues] = React.useState<{
    [key: string]: string;
  }>({});
  const changeFilter = useDispatchFilter();

  const [
    clusterFilterOptionsContext,
    installationFilterOptionsContext,
    materialClassFilterOptionsContext,
    groupFilterOptionsContext,
    descriptionFilterOptionsContext,
  ] = [
    useSelectFilter('cluster'),
    useSelectFilter('installation'),
    useSelectFilter('material-class'),
    useSelectFilter('group'),
    useSelectFilter('description'),
  ];

  const allFilterContexts = [
    clusterFilterOptionsContext,
    installationFilterOptionsContext,
    materialClassFilterOptionsContext,
    groupFilterOptionsContext,
    descriptionFilterOptionsContext,
    updatedAt,
  ];

  const [filterClusterOptions, setFilterClusterOptions] = React.useState<
    IFilterOptions[]
  >([]);

  const [filterMaterialClassOptions, setFilterMaterialClassOptions] =
    React.useState<IFilterOptions[]>([]);

  const [filterGroupOptions, SetFilterGroupOptions] = React.useState<
    IFilterOptions[]
  >([]);

  const [filterGroupDescription, SetFilterGroupDescription] = React.useState<
    IFilterOptions[]
  >([]);

  /** ATIVADORES DOS FILTROS */
  const pauseFilterMaterialClass = React.useMemo<boolean>(() => {
    return materialClassFilterOptionsContext.length > 0;
  }, [materialClassFilterOptionsContext]);

  const pauseFilterGroup = React.useMemo<boolean>(() => {
    return groupFilterOptionsContext.length > 0;
  }, [groupFilterOptionsContext]);

  const pauseFilterDescription = React.useMemo<boolean>(() => {
    return descriptionFilterOptionsContext.length > 0;
  }, [descriptionFilterOptionsContext]);
  /** FIM ATIVADORES DOS FILTROS */

  /** IDS dos filtros selecionados */
  const installationIds = React.useMemo(
    () => installationFilterOptionsContext.map(item => Number(item?.value)),
    [installationFilterOptionsContext],
  );

  const materialClassIds = React.useMemo(
    () => materialClassFilterOptionsContext.map(item => Number(item?.value)),
    [materialClassFilterOptionsContext],
  );

  const groupIds = React.useMemo(
    () => groupFilterOptionsContext.map(item => Number(item?.value)),
    [groupFilterOptionsContext],
  );

  const queryFilterCluster = useQuery({
    queryKey: ['clusterFilterOptionsContext'],
    queryFn: async () => {
      const { data } = await getCluster();

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
          children: item.installations.map(installation => {
            return {
              label: installation?.description,
              value: installation?.id,
            };
          }),
        };
      });
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterClusterOptions(data);
    },
    enabled: true,
  });

  const queryFilterMaterialClass = useQuery({
    queryKey: ['mateiralClassFilterOptionsContext', ...allFilterContexts],
    queryFn: async () => {
      const params = {
        installationIds,
        groupIds,
      };

      const { data } = await postMaterialClassFilterOptions(params);

      return data;
    },
    select: data => {
      return mapFilterResult(data);
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterMaterialClassOptions(data);
    },
    enabled: !pauseFilterMaterialClass,
  });

  const queryFilterGroup = useQuery({
    queryKey: ['GroupFilterOptionsContext', ...allFilterContexts],
    queryFn: async () => {
      const params = {
        installationIds,
        materialClassIds,
      };

      const { data } = await postGroupFilterOptions(params);

      return data;
    },
    select: data => {
      return mapFilterResult(data);
    },
    onSuccess: (data: IFilterOptions[]) => {
      SetFilterGroupOptions(data);
    },
    enabled: !pauseFilterGroup,
  });
  const queryFilterDescription = useInfiniteQuery({
    queryKey: [
      'descriptionFilterOptionsContext',
      [...allFilterContexts, inputFieldValues?.description],
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const qsparams = {
        page: pageParam,
        take: TAKE,
      } as { page: number; take: number };

      const params = {
        installationIds,
        materialClassIds,
        groupIds,
      } as { [key: string]: (string | number)[] | string | number };

      if (
        inputFieldValues?.description &&
        inputFieldValues?.description.length >= 2
      ) {
        params.descriptionFilter = inputFieldValues?.description;
      }

      const { data } = await postDescriptionFilterOptions(
        qs.stringify(qsparams),
        params,
      );
      return data;
    },
    getNextPageParam: (
      lastPage: IStockPositionFilterData,
      allPages: IStockPositionFilterData[],
    ) => {
      const allResults = allPages.map(page => page.result).flat().length;
      if (allResults === 0) return 1;

      const hasNextPage = allResults < lastPage.count;
      if (hasNextPage) return allResults / TAKE + 1;

      return undefined;
    },
    onSuccess: data => {
      const options = data?.pages
        .map(item => item.result)
        .flat()
        .map(item => ({
          label: item.description,
          value: item.id,
        }));
      SetFilterGroupDescription(options);
    },
    enabled: !pauseFilterDescription,
    cacheTime: 0,
  });

  const handleChange = ({
    name,
    value,
  }: {
    name: string;
    value: IFilterOptions[];
  }) => {
    changeFilter(name, value);
  };

  const debouncesetInputFieldValues = React.useMemo(
    () =>
      debounce((name, value) => {
        setInputFieldValues(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <MultipleCheckboxWithSubgroup
                items={filterClusterOptions}
                label="Cluster/Instalação"
                id="cluster-installation"
                selectAllLabel="Selecionar todos"
                groupName="cluster"
                itemName="installation"
                noOptionsText="Nehum resultado encontrado"
                onFilterChange={handleChange}
                loading={queryFilterCluster?.isLoading}
              />
            </Grid>

            <Grid item xs={3} className={classes.inputList}>
              <FilterBarItem
                options={filterMaterialClassOptions}
                onFilterChange={handleChange}
                loading={queryFilterMaterialClass?.isLoading}
                id="material-class"
                name="material-class"
                label="Classe do material"
              />
            </Grid>

            <Grid item xs={3} className={classes.inputList}>
              <FilterBarItem
                options={filterGroupOptions}
                onFilterChange={handleChange}
                loading={queryFilterGroup?.isLoading}
                id="group"
                name="group"
                label="Grupo"
              />
            </Grid>

            <Grid item xs={3} className={classes.inputList}>
              <FilterBarItem
                options={filterGroupDescription}
                onFilterChange={handleChange}
                loading={
                  queryFilterDescription?.isLoading ||
                  queryFilterDescription?.isFetching
                }
                id="description"
                name="description"
                label="Descrição"
                hasNextPage={queryFilterDescription.hasNextPage}
                fetchNextPage={queryFilterDescription.fetchNextPage}
                setInputFieldValue={description => {
                  debouncesetInputFieldValues('description', description);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default StockPositionTopContainer;
